import { API } from '../../services'
import { defaultSorting } from '../../types/Sorting'
import { defineStore } from 'pinia'
import { Filters } from '../../types/Filters'
import { Pagination, defaultPagination } from '../../types/Pagination'
import { setPagination } from '../../services/utils/utils'
import {
  TypeUserLogin,
  TypeUser,
  TypeUserPermission,
  TypeUserInvitation,
  TypeUserModule,
  TypeAuthUserPayload,
  TypeUserAccessUpdate,
} from '../../types/User'
import { useToast, useColors } from 'vuestic-ui'
import router from '../../router'
import { isOkStatus, isSuccessStatus } from '../../utils/http'
import { getSessionManager } from '../../utils/sessionManager'
import { resolveRequiredContextRoute, resolvePostAuthRoute } from '../../utils/contextSelection'
import { parseDashboardKey } from '../../utils/defaultDashboard'

const { init } = useToast()

/** Client Store */
export const useUserStore = defineStore('userStore', {
  // Default Config State
  state: () => ({
    loading: false,
    permissions: [] as TypeUserPermission[],
    userModules: [] as TypeUserModule[],
    is2FAEnabled: false,
    authUser: {} as TypeUser,
    /** Last theme preset applied from auth — skip redundant applyPreset on every nav. */
    appliedTheme: null as string | null,
    /** Backend-resolved dashboard key for the active-business primary role. Never a URL. */
    defaultDashboard: null as string | null,
    users: [] as TypeUser[],
    userInvitations: [] as TypeUserInvitation[],
    filters: {} as Filters,
    pagination: defaultPagination,
    sorting: defaultSorting,
  }),
  // Actions
  actions: {
    initLoading() {
      this.loading = true
    },
    stopLoading() {
      this.loading = false
    },
    setPermissions(permission: TypeUserPermission[]) {
      this.permissions = permission
    },
    setUserModules(modules: TypeUserModule[]) {
      this.userModules = modules
    },
    setAuthUser(user: TypeUser) {
      this.authUser = user
    },

    /** True when authUser was populated from /auth/user or a context endpoint. */
    hasHydratedAuthUser(): boolean {
      return Boolean(this.authUser?.uuid && this.authUser?.email)
    },

    /** Clear in-memory auth so the next protected navigation refetches /auth/user. */
    clearAuthState() {
      this.authUser = {} as TypeUser
      this.permissions = []
      this.userModules = []
      this.appliedTheme = null
      this.defaultDashboard = null
    },

    /** Apply auth payload from /auth/user or active branch/location endpoints. */
    applyAuthPayload(payload: TypeAuthUserPayload) {
      if (payload.user) {
        this.setAuthUser({
          ...payload.user,
          requires_branch_selection: payload.requires_branch_selection ?? payload.user.requires_branch_selection,
          requires_inventory_location_selection:
            payload.requires_inventory_location_selection ?? payload.user.requires_inventory_location_selection,
        })
        this.setUserTheme(payload.user)
      }
      if (payload.permissions) {
        this.setPermissions(payload.permissions)
      }
      if (payload.modules) {
        this.setUserModules(payload.modules)
      }
      this.defaultDashboard = parseDashboardKey(payload.default_dashboard)
    },

    /** Clear branch/location labels so the shell never shows stale context after a business switch. */
    clearActiveBranchAndLocationContext() {
      if (!this.authUser || !Object.keys(this.authUser).length) return
      this.authUser = {
        ...this.authUser,
        active_branch_uuid: null,
        active_branch_name: null,
        active_location_id: null,
        active_inventory_location: null,
        requires_branch_selection: undefined,
        requires_inventory_location_selection: undefined,
      }
    },

    setPagination(pagination: Pagination) {
      this.pagination = setPagination(pagination)
    },

    setUsers(users: TypeUser[]) {
      this.users = users
    },

    setUserInvitations(invitations: TypeUserInvitation[]) {
      this.userInvitations = invitations
    },

    addUserInvitation(invitation: TypeUserInvitation) {
      this.userInvitations.push(invitation)
    },

    removeUserInvitation(invitationUuid: string) {
      const index = this.userInvitations.findIndex((invitation) => invitation.uuid === invitationUuid)
      if (index !== -1) {
        this.userInvitations.splice(index, 1)
      }
    },

    toggle2FA() {
      this.is2FAEnabled = !this.is2FAEnabled
    },

    hasPermission(permission: keyof TypeUserPermission, obj: string): boolean {
      const permissionFound = this.permissions.find(
        (permiss: TypeUserPermission) => permiss.name === obj && permiss[permission] == 1,
      )
      return permissionFound ? true : false
    },

    hasModule(slug: string): boolean {
      const moduleFound = this.userModules.find((module: TypeUserModule) => module.slug === slug)

      return moduleFound ? true : false
    },

    // Users methods.
    async dispatchGet(): Promise<any> {
      this.initLoading()
      const { status, data } = await API.user.getUsers(this.filters, this.pagination).finally(this.stopLoading)

      if (isOkStatus(status)) {
        // set users
        this.setUsers(data.data)
        this.setPagination({
          meta: data.meta,
        })
      }

      return {
        success: isOkStatus(status),
      }
    },

    async dispatchUserLogin(input: TypeUserLogin): Promise<void> {
      // Check if another tab is already logged in
      const sessionManager = getSessionManager()
      if (sessionManager.hasOtherTabSession()) {
        init({
          message: 'You are already logged in another tab. Please close this tab or use the existing session.',
          color: 'warning',
        })
        return
      }

      this.initLoading()
      try {
        const { status } = await API.user.userLogin(input)
        if (!isOkStatus(status)) return

        // Drop any stale SPA auth from a prior session before the post-login cascade.
        this.clearAuthState()
        // Claim this tab immediately so another tab cannot sign in during /auth/user.
        sessionManager.startSession()

        let authenticated = false
        try {
          authenticated = await this.dispatchGetAuthenticatedUser({ force: true })
        } catch {
          authenticated = false
        }

        if (!authenticated) {
          sessionManager.endSession()
          init({ message: 'Could not load your account. Please try again.', color: 'danger' })
          return
        }

        init({ message: 'Logged in successfully', color: 'success' })

        // Context pickers first; otherwise the primary-role dashboard for this business.
        router.replace({
          name: resolvePostAuthRoute(
            resolveRequiredContextRoute(this.authUser),
            this.defaultDashboard,
            (slug) => this.hasModule(slug),
            (action, subject) => this.hasPermission(action, subject),
          ),
        })
      } catch {
        // Login 401/network: interceptor already toasted. Do not rethrow to the form.
      } finally {
        this.stopLoading()
      }
    },

    /**
     * Ensure authUser is available.
     * @param options.force - when true, always hit /auth/user. When false (router guard),
     *   reuse a hydrated authUser so rapid post-login navigations do not stack awaits.
     */
    async dispatchGetAuthenticatedUser(options?: { force?: boolean }): Promise<boolean> {
      const force = options?.force === true
      const sessionManager = getSessionManager()

      // Check if another tab already has an active session
      // This prevents multiple tabs from being logged in simultaneously
      if (sessionManager.hasOtherTabSession()) {
        init({
          message: 'You are already logged in another tab. Please close this tab or use the existing session.',
          color: 'warning',
        })
        // Let the router guard perform the redirect.
        return false
      }

      if (!force && this.hasHydratedAuthUser()) {
        sessionManager.startSession()
        return true
      }

      try {
        const { status, data } = await API.user.getAuthenticatedUser()

        if (isOkStatus(status)) {
          // If user is authenticated, start/update session for this tab
          // This handles the case where user refreshes the page (tab ID changes)
          sessionManager.startSession()

          this.applyAuthPayload(data)
          // Context bootstrap redirects are handled by the router guard on requiresAuth routes.
          return true
        }
      } catch {
        this.clearAuthState()
        return false
      }

      this.clearAuthState()
      return false
    },

    setUserTheme(user: TypeUser) {
      const theme = user.user_preferences?.theme ?? 'light'
      if (this.appliedTheme === theme) return
      useColors().applyPreset(theme)
      this.appliedTheme = theme
    },

    async dispatchGetUserInvitationOfUuid(uuid: string) {
      this.initLoading()
      const { status, data } = await API.user.getUserInvitationOfUuid(uuid).finally(this.stopLoading)

      if (isOkStatus(status)) {
        return data.data
      }
    },

    async dispatchUpdateUserAccess(userUuid: string, payload: TypeUserAccessUpdate) {
      this.initLoading()
      const { status } = await API.user.updateUserAccess(userUuid, payload).finally(this.stopLoading)

      if (isOkStatus(status)) {
        init({ message: 'User access updated successfully', color: 'success' })
        await this.dispatchGet()
      }

      return {
        success: isOkStatus(status),
      }
    },

    async dispatchSendUserInvitation(userData: Partial<TypeUserInvitation>) {
      this.initLoading()
      const { status, data } = await API.user.sendUserInvitation(userData).finally(this.stopLoading)

      if (isSuccessStatus(status)) {
        init({ message: 'User invited successfully', color: 'success' })

        this.addUserInvitation(data.data)
      }

      return {
        success: isSuccessStatus(status),
      }
    },

    async dispatchGetUserInvitations(filters?: Filters): Promise<any> {
      this.initLoading()
      const { status, data } = await API.user.getUserInvitations(filters, this.pagination).finally(this.stopLoading)

      if (isOkStatus(status)) {
        this.setUserInvitations(data.data)
        this.setPagination({
          meta: data.meta,
        })
      }

      return {
        success: isOkStatus(status),
        data: data.data,
      }
    },

    async dispatchDeleteUserInvitation(userInvitation: TypeUserInvitation) {
      this.initLoading()
      const { status } = await API.user.deleteUserInvitation(userInvitation.uuid).finally(this.stopLoading)

      if (isSuccessStatus(status)) {
        init({ message: 'User invitation deleted successfully', color: 'success' })
        this.removeUserInvitation(userInvitation.uuid)
      }
    },

    async dispatchActivateUserInvitation(invitationUuid: string, password: string) {
      this.initLoading()
      const { status } = await API.user.activateUserInvitation(invitationUuid, password).finally(this.stopLoading)

      if (isSuccessStatus(status)) {
        init({ message: 'User invitation activated successfully. Redirecting to login...', color: 'success' })

        setTimeout(() => {
          router.push({ name: 'login' })
        }, 4000)
      }
    },

    async dispatchSendResetPasswordLink(email: string, equestSource: number) {
      this.initLoading()
      const { status } = await API.user.sendResetPasswordLink(email, equestSource).finally(this.stopLoading)

      return {
        success: isSuccessStatus(status),
      }
    },

    async dispatchResetPassword(token: string, email: string, password: string, confirmPassword: string) {
      this.initLoading()
      const { status } = await API.user.resetPassword(token, email, password, confirmPassword).finally(this.stopLoading)

      return {
        success: isSuccessStatus(status),
      }
    },
  },
})
